'use client';

import { useState, useEffect, useRef } from 'react';
import { Link } from '@/components/LocaleLink';
import { useLocale } from '@/hooks/useLocale';

const CONSENT_KEY = 'autofrance-cookie-consent';

type ConsentLevel = 'all' | 'necessary' | null;

export function CookieConsent() {
  // Start visible so banner is in SSR HTML (paints at FCP, not after hydration).
  // Inline <script> in <head> adds .cc-done class to hide it for returning users.
  const [visible, setVisible] = useState(true);
  const bannerRef = useRef<HTMLDivElement>(null);
  const { locale } = useLocale();
  const isSe = locale === 'sv';

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY) as ConsentLevel;
    if (consent) {
      setVisible(false);
    }
  }, []);

  // Bannern är position:fixed längst ned och täcker därför alltid det som råkar ligga
  // där. I kassan blev det Förnamn och Efternamn — de första obligatoriska fälten —
  // vilket mättes upp 2026-09-02. Vi reserverar lika mycket utrymme längst ned på
  // sidan som bannern tar, så den lägger sig över tomrum i stället för över innehåll.
  useEffect(() => {
    if (!visible) {
      document.body.style.paddingBottom = '';
      return;
    }
    const apply = () => {
      const h = bannerRef.current?.offsetHeight ?? 0;
      document.body.style.paddingBottom = h ? `${h}px` : '';
    };
    apply();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
    if (ro && bannerRef.current) ro.observe(bannerRef.current);
    window.addEventListener('resize', apply);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', apply);
      document.body.style.paddingBottom = '';
    };
  }, [visible]);

  const handleAccept = (level: 'all' | 'necessary') => {
    localStorage.setItem(CONSENT_KEY, level);
    // Hide banner via injected style (avoids modifying <html> attributes which breaks React hydration)
    const s = document.createElement('style');
    s.textContent = '.cc-banner{display:none!important}';
    document.head.appendChild(s);

    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      const granted = level === 'all' ? 'granted' : 'denied';
      window.gtag('consent', 'update', {
        analytics_storage: granted,
        ad_storage: granted,
        ad_user_data: granted,
        ad_personalization: granted,
      });
    }

    const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/api';
    fetch(`${apiUrl}/consent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ choice: level }),
    }).catch(() => {});

    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div ref={bannerRef} className="cc-banner fixed inset-x-0 z-[100] pointer-events-none"
      // --af-bottom-bar sätts av sidor som har en egen fast bottenrad (kassans
      // betalrad). Utan detta la sig bannern ovanpå den. 3.25rem är den mobila
      // bottennavigeringens höjd, som gäller på alla andra sidor.
      style={{ bottom: 'calc(var(--af-bottom-bar, 3.25rem) + env(safe-area-inset-bottom))' }}>
      <div className="pointer-events-auto bg-background/98 backdrop-blur-md border-t shadow-lg px-4 py-3">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
          <p className="text-sm text-muted-foreground flex-1">
            {isSe ? 'Vi använder cookies för varukorg, fordon och sökhistorik.' : 'We use cookies for cart, vehicle and search history.'}{' '}
            <Link href="/cookies" className="text-primary hover:underline">{isSe ? 'Läs mer om cookies' : 'Read more about cookies'}</Link>
          </p>
          <div className="flex gap-2 shrink-0 w-full sm:w-auto">
            <button
              onClick={() => handleAccept('all')}
              className="flex-1 sm:flex-none px-4 py-2 text-sm font-semibold rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors min-h-[44px]"
            >
              {isSe ? 'Acceptera' : 'Accept'}
            </button>
            <button
              onClick={() => handleAccept('necessary')}
              className="flex-1 sm:flex-none px-4 py-2 text-sm rounded-md border border-input bg-background hover:bg-accent transition-colors min-h-[44px]"
            >
              {isSe ? 'Bara nödvändiga' : 'Necessary only'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
